import client from 'client'
import type {
  GetAssetConfigurationAssetAssetIdConfigurationGetQueryResponse,
  GetAssetConfigurationAssetAssetIdConfigurationGetPathParams,
  GetAssetConfigurationAssetAssetIdConfigurationGetQueryParams,
  GetAssetConfigurationAssetAssetIdConfigurationGet422,
} from '../../types/GetAssetConfigurationAssetAssetIdConfigurationGet.ts'
import type { QueryKey, QueryObserverOptions, UseQueryResult } from '@tanstack/react-query'
import type { RequestConfig, ResponseErrorConfig, ResponseConfig } from 'client'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getAssetConfigurationAssetAssetIdConfigurationGetQueryKey = (
  asset_id: GetAssetConfigurationAssetAssetIdConfigurationGetPathParams['asset_id'],
  params: GetAssetConfigurationAssetAssetIdConfigurationGetQueryParams,
) => [{ url: '/asset/:asset_id/configuration', params: { asset_id: asset_id } }, ...(params ? [params] : [])] as const

export type GetAssetConfigurationAssetAssetIdConfigurationGetQueryKey = ReturnType<typeof getAssetConfigurationAssetAssetIdConfigurationGetQueryKey>

/**
 * @summary Get Asset Configuration
 * {@link /asset/:asset_id/configuration}
 */
export async function getAssetConfigurationAssetAssetIdConfigurationGet(
  asset_id: GetAssetConfigurationAssetAssetIdConfigurationGetPathParams['asset_id'],
  params: GetAssetConfigurationAssetAssetIdConfigurationGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    GetAssetConfigurationAssetAssetIdConfigurationGetQueryResponse,
    ResponseErrorConfig<GetAssetConfigurationAssetAssetIdConfigurationGet422>,
    unknown
  >({ method: 'GET', url: `/asset/${asset_id}/configuration`, params, ...requestConfig })
  return res
}

export function getAssetConfigurationAssetAssetIdConfigurationGetQueryOptions(
  asset_id: GetAssetConfigurationAssetAssetIdConfigurationGetPathParams['asset_id'],
  params: GetAssetConfigurationAssetAssetIdConfigurationGetQueryParams,
  config: Partial<RequestConfig> & { client?: typeof client } = {},
) {
  const queryKey = getAssetConfigurationAssetAssetIdConfigurationGetQueryKey(asset_id, params)
  return queryOptions<
    ResponseConfig<GetAssetConfigurationAssetAssetIdConfigurationGetQueryResponse>,
    ResponseErrorConfig<GetAssetConfigurationAssetAssetIdConfigurationGet422>,
    ResponseConfig<GetAssetConfigurationAssetAssetIdConfigurationGetQueryResponse>,
    typeof queryKey
  >({
    enabled: !!(asset_id && params),
    queryKey,
    queryFn: async ({ signal }) => {
      config.signal = signal
      return getAssetConfigurationAssetAssetIdConfigurationGet(asset_id, params, config)
    },
  })
}

/**
 * @summary Get Asset Configuration
 * {@link /asset/:asset_id/configuration}
 */
export function useGetAssetConfigurationAssetAssetIdConfigurationGet<
  TData = ResponseConfig<GetAssetConfigurationAssetAssetIdConfigurationGetQueryResponse>,
  TQueryData = ResponseConfig<GetAssetConfigurationAssetAssetIdConfigurationGetQueryResponse>,
  TQueryKey extends QueryKey = GetAssetConfigurationAssetAssetIdConfigurationGetQueryKey,
>(
  asset_id: GetAssetConfigurationAssetAssetIdConfigurationGetPathParams['asset_id'],
  params: GetAssetConfigurationAssetAssetIdConfigurationGetQueryParams,
  options: {
    query?: Partial<
      QueryObserverOptions<
        ResponseConfig<GetAssetConfigurationAssetAssetIdConfigurationGetQueryResponse>,
        ResponseErrorConfig<GetAssetConfigurationAssetAssetIdConfigurationGet422>,
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: Partial<RequestConfig> & { client?: typeof client }
  } = {},
) {
  const { query: queryOptions, client: config = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? getAssetConfigurationAssetAssetIdConfigurationGetQueryKey(asset_id, params)

  const query = useQuery({
    ...(getAssetConfigurationAssetAssetIdConfigurationGetQueryOptions(asset_id, params, config) as unknown as QueryObserverOptions),
    queryKey,
    ...(queryOptions as unknown as Omit<QueryObserverOptions, 'queryKey'>),
  }) as UseQueryResult<TData, ResponseErrorConfig<GetAssetConfigurationAssetAssetIdConfigurationGet422>> & { queryKey: TQueryKey }

  query.queryKey = queryKey as TQueryKey

  return query
}